import React from "react";
import {css, Theme} from "@emotion/react";

import {display, flex, mr, p, pb, pl, pt, truncate, w100} from "@pg-design/helpers-css";
import {CalendarCheckIcon, CloseIcon, MapMarkerOutlineIcon, PriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useHidePricesAbTest} from "../../hooks/use_hide_prices_ab_test";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {Country} from "../../region/types/Country";
import {sidebarInvestmentClickHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {getCurrency} from "../../utils/get_currency";
import {ViewType} from "../../view_type/ViewType";
import {useOfferLink} from "../detail/hooks/use_offer_link";
import {IMapOfferSidebar} from "../map/types/MapOfferSidebar";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {OfferPriceType} from "../types/OfferPriceType";

interface IProps {
    offer: IMapOfferSidebar;
    onOfferSidebarClose: () => void;
}

export const OfferSidebarBox = (props: IProps) => {
    const {offer, onOfferSidebarClose} = props;
    const offerLink = useOfferLink(props.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: offer.vendor.slug, vendorId: offer.vendor.id});
    const target = useResponsiveLinkTarget();
    const {isMobile} = useUserDevice();

    const {isHiddenVariant} = useHidePricesAbTest();
    const {data: userInfo} = useGetUserProfileQuery();

    const shouldShowPrice = !isHiddenVariant || Boolean(!isHiddenVariant && userInfo && userInfo.phone);

    const onSidebarBoxClick = () => {
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.INVEST);
        sidebarInvestmentClickHit(ViewType.OFFER_LIST_MAP, offer.id);
    };

    const onVendorClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();

        window.open(vendorLink, target);
    };
    const offerRangePrice =
        (props.offer.region.country === Country.SPAIN && props.offer?.stats?.ranges_price_min) || props.offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    return (
        <>
            <div css={closeHolder} onClick={onOfferSidebarClose}>
                <CloseIcon size="2" />
            </div>

            <a href={offerLink} onClick={onSidebarBoxClick} css={[sidebarOfferLink]} title={offer.name} target={target}>
                <div css={[display("flex")]}>
                    <div css={sideBarImageHeader}>
                        <Image alt={offer.name} src={offer.main_image.m_img_224x147} width="243" height="160" loading="lazy" ratio={{xs: "243/160"}} />
                    </div>

                    <div css={detailsWrapper}>
                        <Text as="h2" variant="headline_6" className="bt">
                            {offer.name}
                        </Text>

                        <Text css={link} mb={2} as="h3" variant="info_txt_1">
                            <span onClick={onVendorClick} css={truncate} className="bt">
                                {offer.vendor.name}
                            </span>
                        </Text>

                        <div css={flex()}>
                            <MapMarkerOutlineIcon size="2" css={mr(1)} />

                            <Text as="div" variant="info_txt_2" css={[link, distanceFromText, w100]}>
                                <span>
                                    {offer?.region.short_name_reverted}, {offer.street_name} {offer.street_number && offer.street_number}
                                </span>
                            </Text>
                        </div>

                        {!isMobile && (
                            <>
                                {shouldShowPrice && offerRangePrice && (
                                    <Text as="div" variant="info_txt_2" mb={1.5}>
                                        <PriceIcon size="2" css={mr(1)} />
                                        Cena:{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od{" "}
                                            <span className="bt">
                                                {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                                {priceType}
                                                {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                                    <>
                                                        /m<sup>2</sup>
                                                    </>
                                                )}
                                            </span>
                                        </Text>
                                    </Text>
                                )}
                                <Text as="div" variant="info_txt_2" mb={1.5}>
                                    <CalendarCheckIcon size="2" css={mr(1)} />
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </>
                        )}
                    </div>
                </div>
                {isMobile && (
                    <div css={[offerDetailHolder, pt(2)]}>
                        <div>
                            {offerRangePrice && (
                                <div css={[alignVertically, pb(2)]}>
                                    <PriceIcon size="2" />

                                    <Text as="div" variant="info_txt_2" css={[pl(2)]}>
                                        Cena{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od:
                                            {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                            {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                                <>
                                                    /m<sup>2</sup>
                                                </>
                                            )}
                                        </Text>
                                    </Text>
                                </div>
                            )}

                            <div css={[alignVertically, pb(2)]}>
                                <CalendarCheckIcon size="2" />
                                <Text as="div" variant="info_txt_2" css={[pl(2)]}>
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </div>
                        </div>
                    </div>
                )}
            </a>
        </>
    );
};

const sidebarOfferLink = (theme: Theme) => css`
    display: block;
    ${p(3, 3, 0, 3)};

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(0)}
    }
`;

const sideBarImageHeader = (theme: Theme) => css`
    position: relative;
    width: 104px;
    flex-shrink: 0;
    ${mr(1)};

    @media (min-width: ${theme.breakpoints.md}) {
        width: 243px;
        ${mr(2)};
    }
`;

const detailsWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    width: 100%;

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 50%;
    }
`;

const link = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};

    &:hover,
    &:focus {
        color: inherit;
    }
`;

const offerDetailHolder = (theme: Theme) => css`
    display: flex;
    min-height: 7.2rem;
    justify-content: space-between;
    @media (min-width: ${theme.breakpoints.xs}) {
      padding-top: 3rem
    }
}`;

const alignVertically = css`
    ${display("flex")}
    align-items: center;
`;

const distanceFromText = (theme: Theme) => css`
    margin-bottom: 0;
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 2.8rem;
    }
`;

const closeHolder = (theme: Theme) => css`
    cursor: pointer;
    position: absolute;
    right: 2rem;
    top: 0;

    @media (min-width: ${theme.breakpoints.xs}) {
        top: 2rem;
    }
`;
import React from "react";
import {css, Theme} from "@emotion/react";

import {display, flex, mr, p, pb, pl, pt, truncate, w100} from "@pg-design/helpers-css";
import {CalendarCheckIcon, CloseIcon, MapMarkerOutlineIcon, PriceIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useHidePricesAbTest} from "../../hooks/use_hide_prices_ab_test";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {Country} from "../../region/types/Country";
import {sidebarInvestmentClickHit} from "../../tracking/algolytics_hits/map_interaction_hits";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {getCurrency} from "../../utils/get_currency";
import {ViewType} from "../../view_type/ViewType";
import {useOfferLink} from "../detail/hooks/use_offer_link";
import {IMapOfferSidebar} from "../map/types/MapOfferSidebar";
import {gtmEventMapList, MapGTMActions, MapGTMLabel} from "../map/utils/map_analytics";
import {OfferPriceType} from "../types/OfferPriceType";

interface IProps {
    offer: IMapOfferSidebar;
    onOfferSidebarClose: () => void;
}

export const OfferSidebarBox = (props: IProps) => {
    const {offer, onOfferSidebarClose} = props;
    const offerLink = useOfferLink(props.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: offer.vendor.slug, vendorId: offer.vendor.id});
    const target = useResponsiveLinkTarget();
    const {isMobile} = useUserDevice();

    const {isHiddenVariant} = useHidePricesAbTest();
    const {data: userInfo} = useGetUserProfileQuery();

    const shouldShowPrice = !isHiddenVariant || Boolean(!isHiddenVariant && userInfo && userInfo.phone);

    const onSidebarBoxClick = () => {
        gtmEventMapList(MapGTMActions.SIDEBAR, MapGTMLabel.INVEST);
        sidebarInvestmentClickHit(ViewType.OFFER_LIST_MAP, offer.id);
    };

    const onVendorClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();

        window.open(vendorLink, target);
    };
    const offerRangePrice =
        (props.offer.region.country === Country.SPAIN && props.offer?.stats?.ranges_price_min) || props.offer.stats?.ranges_price_m2_min || null;

    const priceType = offer.region.country === Country.SPAIN && offer.price_type === OfferPriceType.NETTO ? " netto" : "";

    return (
        <>
            <div css={closeHolder} onClick={onOfferSidebarClose}>
                <CloseIcon size="2" />
            </div>

            <a href={offerLink} onClick={onSidebarBoxClick} css={[sidebarOfferLink]} title={offer.name} target={target}>
                <div css={[display("flex")]}>
                    <div css={sideBarImageHeader}>
                        <Image alt={offer.name} src={offer.main_image.m_img_224x147} width="243" height="160" loading="lazy" ratio={{xs: "243/160"}} />
                    </div>

                    <div css={detailsWrapper}>
                        <Text as="h2" variant="headline_6" className="bt">
                            {offer.name}
                        </Text>

                        <Text css={link} mb={2} as="h3" variant="info_txt_1">
                            <span onClick={onVendorClick} css={truncate} className="bt">
                                {offer.vendor.name}
                            </span>
                        </Text>

                        <div css={flex()}>
                            <MapMarkerOutlineIcon size="2" css={mr(1)} />

                            <Text as="div" variant="info_txt_2" css={[link, distanceFromText, w100]}>
                                <span>
                                    {offer?.region.short_name_reverted}, {offer.street_name} {offer.street_number && offer.street_number}
                                </span>
                            </Text>
                        </div>

                        {!isMobile && (
                            <>
                                {shouldShowPrice && offerRangePrice && (
                                    <Text as="div" variant="info_txt_2" mb={1.5}>
                                        <PriceIcon size="2" css={mr(1)} />
                                        Cena:{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od{" "}
                                            <span className="bt">
                                                {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                                {priceType}
                                                {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                                    <>
                                                        /m<sup>2</sup>
                                                    </>
                                                )}
                                            </span>
                                        </Text>
                                    </Text>
                                )}
                                <Text as="div" variant="info_txt_2" mb={1.5}>
                                    <CalendarCheckIcon size="2" css={mr(1)} />
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </>
                        )}
                    </div>
                </div>
                {isMobile && (
                    <div css={[offerDetailHolder, pt(2)]}>
                        <div>
                            {offerRangePrice && (
                                <div css={[alignVertically, pb(2)]}>
                                    <PriceIcon size="2" />

                                    <Text as="div" variant="info_txt_2" css={[pl(2)]}>
                                        Cena{" "}
                                        <Text as="span" variant="info_txt_2" strong>
                                            od:
                                            {numberFormat(offerRangePrice)} {getCurrency({country: offer.region.country})}
                                            {props.offer.region.country === Country.SPAIN && props.offer.stats?.ranges_price_min ? null : (
                                                <>
                                                    /m<sup>2</sup>
                                                </>
                                            )}
                                        </Text>
                                    </Text>
                                </div>
                            )}

                            <div css={[alignVertically, pb(2)]}>
                                <CalendarCheckIcon size="2" />
                                <Text as="div" variant="info_txt_2" css={[pl(2)]}>
                                    Termin oddania:{" "}
                                    <Text as="span" variant="info_txt_2" strong>
                                        {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarterLong)}
                                    </Text>
                                </Text>
                            </div>
                        </div>
                    </div>
                )}
            </a>
        </>
    );
};

const sidebarOfferLink = (theme: Theme) => css`
    display: block;
    ${p(3, 3, 0, 3)};

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${p(0)}
    }
`;

const sideBarImageHeader = (theme: Theme) => css`
    position: relative;
    width: 104px;
    flex-shrink: 0;
    ${mr(1)};

    @media (min-width: ${theme.breakpoints.md}) {
        width: 243px;
        ${mr(2)};
    }
`;

const detailsWrapper = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    width: 100%;

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 50%;
    }
`;

const link = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};

    &:hover,
    &:focus {
        color: inherit;
    }
`;

const offerDetailHolder = (theme: Theme) => css`
    display: flex;
    min-height: 7.2rem;
    justify-content: space-between;
    @media (min-width: ${theme.breakpoints.xs}) {
      padding-top: 3rem
    }
}`;

const alignVertically = css`
    ${display("flex")}
    align-items: center;
`;

const distanceFromText = (theme: Theme) => css`
    margin-bottom: 0;
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 2.8rem;
    }
`;

const closeHolder = (theme: Theme) => css`
    cursor: pointer;
    position: absolute;
    right: 2rem;
    top: 0;

    @media (min-width: ${theme.breakpoints.xs}) {
        top: 2rem;
    }
`;
